/**
 * Ludo Move History Component
 *
 * Scrollable panel showing move history in chronological order.
 */

import React, { useEffect, useRef } from 'react';
import { MoveRecord, PlayerColor } from '../types';
import './MoveHistory.css';

interface MoveHistoryProps {
  moves: MoveRecord[];
  maxHeight?: number;
}

const COLOR_EMOJI: Record<PlayerColor, string> = {
  red: '🔴',
  blue: '🔵',
  green: '🟢',
  yellow: '🟡',
};

function formatMove(move: MoveRecord): string {
  const emoji = COLOR_EMOJI[move.player];
  let desc = `${emoji} ${move.player} rolled ${move.diceValue}`;

  if (move.tripleSix) {
    return `${desc} — Triple six! Turn forfeited.`;
  }

  if (move.action === 'enter') {
    desc += ` — Token ${move.tokenIndex + 1} entered the board`;
  } else if (move.action === 'move') {
    if (move.toPosition.location === 'home') {
      desc += ` — Token ${move.tokenIndex + 1} reached home! 🏠`;
    } else if (move.toPosition.location === 'home_stretch') {
      desc += ` — Token ${move.tokenIndex + 1} entered home stretch`;
    } else {
      desc += ` — Token ${move.tokenIndex + 1} moved`;
    }
  } else if (move.action === 'skip') {
    desc += ' — No valid moves, turn skipped';
  }

  if (move.captured) {
    desc += ` 💥 Captured ${move.captured.player} token ${move.captured.tokenIndex + 1}!`;
  }

  if (move.extraTurn) {
    desc += ' ⚡ Extra turn!';
  }

  return desc;
}

const MoveHistory: React.FC<MoveHistoryProps> = ({ moves, maxHeight = 200 }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest move
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [moves.length]);

  if (moves.length === 0) {
    return (
      <div className="ludo-history">
        <h3 className="ludo-history__title">Move History</h3>
        <p className="ludo-history__empty">No moves yet. Roll the dice to start!</p>
      </div>
    );
  }

  return (
    <div className="ludo-history">
      <h3 className="ludo-history__title">Move History</h3>
      <div
        className="ludo-history__list"
        ref={scrollRef}
        style={{ maxHeight }}
        role="log"
        aria-label="Move history"
        aria-live="polite"
      >
        {moves.map((move) => (
          <div
            key={move.moveNumber}
            className={`ludo-history__entry ludo-history__entry--${move.player}`}
          >
            <span className="ludo-history__number">{move.moveNumber}.</span>
            <span className="ludo-history__text">{formatMove(move)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export { MoveHistory };
export default MoveHistory;
